import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  WorkOutline as WorkIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

function JobApplications() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [jobDetails, setJobDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchJobDetails();
    fetchApplications();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const jobDoc = doc(db, 'jobs', jobId);
      const jobSnapshot = await getDoc(jobDoc);
      if (jobSnapshot.exists()) {
        setJobDetails({ id: jobSnapshot.id, ...jobSnapshot.data() });
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
    }
  };

  const fetchApplications = async () => {
    try {
      const q = query(
        collection(db, 'applications'),
        where('jobId', '==', jobId)
      );
      const querySnapshot = await getDocs(q);
      const applicationsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setApplications(applicationsData);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching applications. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (applicationId, newStatus) => {
    try {
      await updateDoc(doc(db, 'applications', applicationId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      setSnackbar({
        open: true,
        message: `Application ${newStatus}!`,
        severity: 'success'
      });
      fetchApplications();
    } catch (error) {
      console.error('Error updating application:', error);
      setSnackbar({
        open: true,
        message: 'Error updating application. Please try again.',
        severity: 'error'
      });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/job-management')}
          sx={{ mb: 2 }}
        >
          Back to career Management
        </Button>
        <Typography variant="h4" component="h1" sx={{ color: '#580100', mb: 1 }}>
          Applications for {jobDetails?.title}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {applications.length} application{applications.length !== 1 ? 's' : ''} received
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f4f4' }}>
                <TableCell>Applicant Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Experience</TableCell>
                <TableCell>Cover Letter</TableCell>
                <TableCell>Applied Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((application) => (
                <TableRow 
                  key={application.id}
                  hover
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                  }}
                >
                  <TableCell onClick={() => setSelectedApplication(application)} sx={{ cursor: 'pointer' }}>
                    {application.name}
                  </TableCell>
                  <TableCell onClick={() => setSelectedApplication(application)} sx={{ cursor: 'pointer' }}>
                    {application.email}
                  </TableCell>
                  <TableCell onClick={() => setSelectedApplication(application)} sx={{ cursor: 'pointer' }}>
                    {application.phone}
                  </TableCell>
                  <TableCell onClick={() => setSelectedApplication(application)} sx={{ cursor: 'pointer' }}>
                    {application.experience}
                  </TableCell>
                  <TableCell onClick={() => setSelectedApplication(application)} sx={{ cursor: 'pointer' }}>
                    <Typography 
                      sx={{ 
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'text.secondary',
                        fontSize: '0.875rem'
                      }}
                    >
                      {application.message || 'No cover letter provided'}
                    </Typography>
                  </TableCell>
                  <TableCell onClick={() => setSelectedApplication(application)} sx={{ cursor: 'pointer' }}>
                    {formatDate(application.timestamp)}
                  </TableCell>
                  <TableCell onClick={() => setSelectedApplication(application)} sx={{ cursor: 'pointer' }}>
                    <Chip
                      label={application.status}
                      color={
                        application.status === 'accepted' ? 'success' :
                        application.status === 'rejected' ? 'error' :
                        'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      href={`mailto:${application.email}`}
                      sx={{ color: '#580100', mr: 1 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EmailIcon />
                    </IconButton>
                    {application.status === 'pending' && (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateStatus(application.id, 'accepted');
                          }}
                          sx={{ color: 'success.main', mr: 1 }}
                        >
                          <CheckIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateStatus(application.id, 'rejected');
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {applications.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      No applications received yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Application Details Modal */}
      <Dialog 
        open={Boolean(selectedApplication)} 
        onClose={() => setSelectedApplication(null)}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
      >
        {selectedApplication && (
          <>
            <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="h6" component="div" sx={{ color: '#580100' }}>
                Application Details
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" component="div">
                      {selectedApplication.name}
                    </Typography>
                    <Chip
                      label={selectedApplication.status}
                      color={
                        selectedApplication.status === 'accepted' ? 'success' :
                        selectedApplication.status === 'rejected' ? 'error' :
                        'default'
                      }
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <EmailIcon color="action" />
                    <Typography>
                      <a href={`mailto:${selectedApplication.email}`} style={{ color: '#580100', textDecoration: 'none' }}>
                        {selectedApplication.email}
                      </a>
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <PhoneIcon color="action" />
                    <Typography>
                      <a href={`tel:${selectedApplication.phone}`} style={{ color: '#580100', textDecoration: 'none' }}>
                        {selectedApplication.phone}
                      </a>
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <WorkIcon color="action" />
                    <Typography>{selectedApplication.experience} years of experience</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <CalendarIcon color="action" />
                    <Typography>Applied on {formatDate(selectedApplication.timestamp)}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#580100' }}>
                    <MessageIcon /> Cover Letter
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2,
                      bgcolor: '#fafafa',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      {selectedApplication.message || 'No cover letter provided.'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid #e0e0e0', px: 3, py: 2 }}>
              <Button
                href={`mailto:${selectedApplication.email}`}
                startIcon={<EmailIcon />}
                sx={{ color: '#580100' }}
              >
                Send Email
              </Button>
              {selectedApplication.status === 'pending' && (
                <>
                  <Button
                    onClick={() => {
                      handleUpdateStatus(selectedApplication.id, 'accepted');
                      setSelectedApplication(null);
                    }}
                    startIcon={<CheckIcon />}
                    variant="contained"
                    color="success"
                  >
                    Accept
                  </Button>
                  <Button
                    onClick={() => {
                      handleUpdateStatus(selectedApplication.id, 'rejected');
                      setSelectedApplication(null);
                    }}
                    startIcon={<CloseIcon />}
                    variant="contained"
                    color="error"
                  >
                    Reject
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default JobApplications;
